/* The scope tables' own bits: the receipt button in a number cell, the "No figure" cell, and the
   small forms. Everything else is the shared components'. Tokens only. */

.figureButton {
  display: inline-flex;
  align-items: center;
  min-height: var(--target-min);
  padding: 0;
  border: 0;
  background: none;
  color: var(--ink);
  font: inherit;
  font-weight: 650;
  font-variant-numeric: lining-nums tabular-nums;
  text-decoration: underline dotted var(--line-2);
  text-underline-offset: 3px;
  cursor: pointer;
}

/* **A figure is ink, never the brand** (the rule at the top of tokens.css: the action colour does not
   paint a number). It says it opens a receipt with a dotted underline, as the tile's figure does, and
   the hover is the one place the accent appears. */
.figureButton:hover {
  color: var(--accent);
  text-decoration-color: currentcolor;
  text-decoration-style: solid;
}

.figureButton:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 3px;
}

.none {
  color: var(--muted);
}

/* The quick-peek body: the figure large, then its facts. */
.peek {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.peekFigure {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  align-self: flex-start;
  padding: 0;
  border: 0;
  background: none;
  color: var(--accent);
  font: inherit;
  font-size: var(--t-figure);
  font-weight: 650;
  font-variant-numeric: lining-nums tabular-nums;
  cursor: pointer;
}

.peekFigure:hover {
  text-decoration: underline;
}

.peekFigure:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 2px;
  border-radius: 3px;
}

.peekUnit {
  color: var(--muted);
  font-size: var(--t-dense);
  font-weight: 600;
}

.peekFacts {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 0;
}

.peekFact {
  display: grid;
  grid-template-columns: 6rem 1fr;
  gap: 8px;
  align-items: center;
  font-size: var(--t-dense);
}

.peekFact dt {
  color: var(--muted);
}

.peekFact dd {
  margin: 0;
}

.tableNote {
  padding: 10px 14px;
  color: var(--muted);
  font-size: var(--t-dense);
}

.headLink {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: var(--h-ctl);
  padding: 0 12px;
  border: 1px solid var(--accent);
  border-radius: var(--radius-ctl);
  background: var(--accent);
  color: var(--on-fill);
  font-size: var(--t-dense);
  font-weight: 650;
  text-decoration: none;
}

.headLink:hover {
  background: var(--accent-h);
}

/* ---------------------------------------------------------------- the Add drawer's form */

.form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: var(--t-dense);
  font-weight: 600;
}

.field select,
.field input,
.field textarea {
  min-height: var(--h-ctl);
  padding: 4px 8px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-weight: 400;
}

.field textarea {
  min-height: 72px;
  resize: vertical;
}

.pair {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
}

.hint {
  margin: 0;
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 400;
}

.actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
}

/* The figure and what it rests on stay on one line: a two-line cell made every row of a long register
   seventy pixels tall. */
.figureCell {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  white-space: nowrap;
}

.railList {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
  font-size: var(--t-dense);
}

/* The file name takes what is left and the button its own width: `anywhere` on the row let the button's
   word break ("Op" over "en") (alignment audit, 2026-11-03). */
.railDoc {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 8px;
  font-size: var(--t-dense);
  overflow-wrap: anywhere;
}

.railDoc button {
  white-space: nowrap;
  overflow-wrap: normal;
}

/* The replacement-bill link takes its own row under the file name and its Open button. */
.railDoc a {
  grid-column: 1 / -1;
  justify-self: start;
}

.primary {
  min-height: var(--h-ctl);
  padding: 0 14px;
  border: 1px solid var(--accent);
  border-radius: var(--radius-ctl);
  background: var(--accent);
  color: var(--on-fill);
  font: inherit;
  font-size: var(--t-dense);
  font-weight: 650;
  cursor: pointer;
}

.primary:disabled {
  border-color: var(--btn-off-bg);
  background: var(--btn-off-bg);
  color: var(--btn-off-ink);
  cursor: not-allowed;
}

.ghost {
  min-height: var(--h-ctl);
  padding: 0 12px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  color: var(--ink);
  font: inherit;
  font-size: var(--t-dense);
  cursor: pointer;
}

.done {
  padding: 10px 12px;
  border: 1px solid var(--ok-line);
  border-radius: var(--radius-chip);
  background: var(--ok-w);
  color: var(--ok);
  font-size: var(--t-dense);
}

.refusal {
  padding: 10px 12px;
  border: 1px solid var(--crit-line);
  border-radius: var(--radius-chip);
  background: var(--crit-w);
  color: var(--crit);
  font-size: var(--t-dense);
}

/* ------------------------------------------------------------------ the record's calculation */

.sum {
  display: flex;
  flex-wrap: wrap;
  align-items: stretch;
  gap: 8px;
  padding: 14px;
}

.box {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 120px;
  padding: 8px 12px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-chip);
  background: var(--panel-2);
}

.box small {
  color: var(--muted);
  font-size: var(--t-min);
  font-weight: 600;
}

.box b {
  font-size: var(--t-head);
  font-variant-numeric: lining-nums tabular-nums;
}

.box span {
  color: var(--muted);
  font-size: var(--t-min);
}

.box[data-result] {
  border-color: var(--accent-line);
  background: var(--accent-w);
}

.op {
  align-self: center;
  color: var(--muted);
  font-size: var(--t-head);
  font-weight: 650;
}

.trail {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 12px 14px;
  margin: 0;
  list-style: none;
}

.cardHead {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 14px;
  border-bottom: 1px solid var(--line);
  font-size: var(--t-head);
  font-weight: 650;
}

.sp {
  flex: 1 1 auto;
}

.cardBody {
  padding: 12px 14px;
}

/* The line under a table that holds part of a selection: how many rows are listed of how many exist,
   and the button that lists the next page. Quiet - it is a status, not a message. */
.showMore {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--gap);
  padding: var(--gap) var(--pad);
  color: var(--muted);
  font-size: var(--t-dense);
}
